import { Component, Property, TextComponent, MeshComponent } from '@wonderlandengine/api';

// Pre-allocate common strings to prevent JS Garbage Collection pauses during gameplay
const LEVEL_STRING_CACHE = new Array(200);
for (let i = 0; i < 200; i++) {
    LEVEL_STRING_CACHE[i] = i.toString();
}
const LEVEL_REGEX = /^\d+$/;

export class LevelSync extends Component {
    static TypeName = 'level-sync';

    static Properties = {
        levelTextObject: Property.object(), // The text object to display the level
        prestigeSymbol1: Property.object(), // Levels 1-25 symbol
        prestigeSymbol2: Property.object(), // Prestige 1 symbol  
        prestigeSymbol3: Property.object(), // Prestige 2 symbol
        prestigeSymbol4: Property.object(), // Prestige 3 symbol
    };

    start() {
        this.currentLevel = 1;
        this.levelText = null;
        this._lastLevelText = ''; // Cache text state to avoid WASM bridge calls
        this._currentPrestige = -1; // Cache prestige state
        
        this._findLevelText();
        
        this.updateLevelDisplay();

        if (this.engine && this.engine.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        // Listen for desktop session end (EXIT button)
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
    }

    onDestroy() {
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        }
    }

    onDesktopSessionEnd() {
        this._resetSession();
    }

    onXRSessionEnd() {
        this._resetSession();
    }

    _resetSession() {
        // Reset level to default
        this.currentLevel = 1;

        // Clear level text to prevent ghost UI in lobby
        if (this.levelText && this._lastLevelText !== '') {
            this.levelText.text = '';
            this._lastLevelText = '';
        }

        // Hide all prestige symbols efficiently
        if (this._prestigeMeshCache && this._currentPrestige !== -1) {
            for (let i = 0; i < this._prestigeMeshCache.length; i++) {
                if (this._prestigeMeshCache[i]) this._prestigeMeshCache[i].active = false;
            }
            this._currentPrestige = -1;
        }
    }

    /**
     * Set the level for this remote player
     * @param {number} level - The player's level
     */
    setLevel(level) {
        if (typeof level !== 'number' || level < 1) {
            const objName = this.object ? this.object.name : 'unnamed';
            console.warn('[LevelSync] Invalid level received:', level, 'type:', typeof level, 'for object:', objName);
            return;
        }
        
        // Store the level even if we don't have text component yet
        this.currentLevel = level;
        
        // Try to find text component if we don't have it yet (but don't re-scan if already searched)
        if (!this.levelText && !this._levelTextSearched) {
            this._findLevelText();
        }
        
        this.updateLevelDisplay();
    }

    /**
     * Helper method to find the level text component
     */
    _findLevelText() {
        if (this.levelTextObject) {
            this.levelText = this.levelTextObject.getComponent(TextComponent);
        }
        
        if (!this.levelText && this.object) {
            // CACHE `this.object.children` to prevent JS-WASM bridge array allocation in loops
            const children = this.object.children;
            if (children) {
                const len = children.length;
                
                // PERFORMANCE: Use for loop instead of .find() and cache WASM getters
                for (let i = 0; i < len; i++) {
                    const child = children[i];
                    const childName = child.name; // Get name once to avoid crossing bridge repeatedly
                    if (childName === "PlayerLevel" || childName === "Level" || 
                        childName === "PlayerLevelText" || childName === "LevelText") {
                        this.levelText = child.getComponent(TextComponent);
                        break;
                    }
                }
                
                // Last resort: find any TextComponent that might be for level display
                if (!this.levelText) {
                    for (let i = 0; i < len; i++) {
                        const child = children[i];
                        const textComp = child.getComponent(TextComponent);
                        if (textComp) {
                            const text = textComp.text; // Get text once to avoid crossing bridge repeatedly
                            if (text) {
                                const lowerText = text.toLowerCase();
                                if (lowerText.includes('lvl') || lowerText.includes('level') || LEVEL_REGEX.test(text)) {
                                    this.levelText = textComp;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // Mark as searched so we don't re-scan on every setLevel() call
        this._levelTextSearched = true;
        if (!this.levelText) {
            const objName = this.object ? this.object.name : 'unnamed';
            console.warn('[LevelSync] Could not find any suitable TextComponent for level display on object:', objName);
        }
    }

    /**
     * Update the visual display of the level
     */
    updateLevelDisplay() {
        if (this.levelText && typeof this.currentLevel === 'number') {
            // Fetch from pre-allocated string cache if under level 200, otherwise fallback to toString
            const newText = (this.currentLevel >= 0 && this.currentLevel < 200) 
                ? LEVEL_STRING_CACHE[this.currentLevel] 
                : this.currentLevel.toString();
            
            // Cross JS-WASM bridge ONLY if text has actually changed
            if (this._lastLevelText !== newText) {
                this.levelText.text = newText;
                this._lastLevelText = newText;
            }
        } else {
            console.warn('[LevelSync] Cannot update display - levelText:', !!this.levelText, 'currentLevel:', this.currentLevel, 'type:', typeof this.currentLevel);
        }
    }

    /**
     * Get the current level
     * @returns {number} The current level
     */
    getLevel() {
        return this.currentLevel;
    }

    /**
     * Set the prestige level for this remote player
     * @param {number} prestige - The player's prestige level (0-3)
     */
    setPrestige(prestige) {
        if (typeof prestige !== 'number' || prestige < 0 || prestige > 3) {
            const objName = this.object ? this.object.name : 'unnamed';
            console.warn('[LevelSync] Invalid prestige received:', prestige, 'type:', typeof prestige, 'for object:', objName);
            return;
        }

        // Return early to avoid WASM boundary and computation overhead if state unchanged
        if (this._currentPrestige === prestige) return;

        // APP TIME OPTIMIZATION: Cache MeshComponent lookups
        if (!this._prestigeMeshCache) {
            this._prestigeMeshCache = [
                this.prestigeSymbol1 ? this.prestigeSymbol1.getComponent(MeshComponent) : null,
                this.prestigeSymbol2 ? this.prestigeSymbol2.getComponent(MeshComponent) : null,
                this.prestigeSymbol3 ? this.prestigeSymbol3.getComponent(MeshComponent) : null,
                this.prestigeSymbol4 ? this.prestigeSymbol4.getComponent(MeshComponent) : null,
            ];
            
            // First time init: ensure all are deactivated explicitly
            for (let i = 0; i < this._prestigeMeshCache.length; i++) {
                if (this._prestigeMeshCache[i]) {
                    this._prestigeMeshCache[i].active = false;
                }
            }
        } else if (this._currentPrestige >= 0 && this._currentPrestige < this._prestigeMeshCache.length) {
            // Target only the previously active mesh for deactivation (1 WASM call instead of 4)
            const prevMesh = this._prestigeMeshCache[this._currentPrestige];
            if (prevMesh) prevMesh.active = false;
        }

        // Target only the new mesh for activation (1 WASM call)
        if (prestige >= 0 && prestige < this._prestigeMeshCache.length) {
            const newMesh = this._prestigeMeshCache[prestige];
            if (newMesh) newMesh.active = true;
        }

        this._currentPrestige = prestige;
    }
}